// Main Editor Application
import { ModalManager } from './modal_manager.js';
import { StateManager } from './state_manager.js';
import { UIManager } from './ui_manager.js';
import { PhotoManager } from './photo_manager.js';
import { FolderManager } from './folder_manager.js';
import { KeyboardManager } from './keyboard_manager.js';
import { SliderManager } from './slider_manager.js';
import { ViewManager } from './view_manager.js';
import { PhotoRender } from './photo_render.js';
import { DropdownMenu } from './dropdown_menu.js';
import { ExportQueueManager } from './export_queue_manager.js';
import { ExportRenderer } from './export_renderer.js';

export class EditorApp {
  constructor() {
    // Initialize managers
    this.modalManager = new ModalManager();
    this.stateManager = new StateManager();
    this.editorState = this.stateManager.editorState; // Direct reference to reactive state
    this.uiManager = new UIManager(this.stateManager, null); // Will set photoManager after creation
    this.photoManager = new PhotoManager(this.stateManager, this.uiManager);
    this.sliderManager = new SliderManager(this.stateManager, this.photoManager);
    this.viewManager = new ViewManager(this.stateManager, this.photoManager, this.sliderManager);
    this.folderManager = new FolderManager(this.stateManager, this.photoManager, this.uiManager, this.modalManager);
    this.keyboardManager = new KeyboardManager(this.stateManager, this.photoManager, this.uiManager, this.modalManager, this.sliderManager);
    this.exportQueueManager = new ExportQueueManager(this.editorState);
    this.exportRenderer = new ExportRenderer(this.editorState, this.exportQueueManager);
    
    // Set photoManager reference in uiManager
    this.uiManager.photoManager = this.photoManager;
    // Set sliderManager reference in uiManager
    this.uiManager.sliderManager = this.sliderManager;
    // Set viewManager reference in uiManager and sliderManager
    this.uiManager.setViewManager(this.viewManager);
    this.sliderManager.setViewManager(this.viewManager);
    
    // PhotoRender instance for Edit page
    this.currentPhotoRender = null;
    
    // Dropdown menus
    this.previewOptionsDropdown = null;
    this.userMenuDropdown = null;
  }

  /**
   * Initialize the application
   */
  async init() {
    try {
      console.log('EditorApp initializing...');
      
      // Store instance globally for access from other parts of the app
      window.editorApp = this;
      
      // Make PhotoRender available globally for testing
      window.PhotoRender = PhotoRender;
      
      // Parse URL FIRST to determine which page to show (before any rendering)
      const urlState = this.stateManager.parseUrl();
      
      // Pre-hide pages that shouldn't be shown based on URL
      // This prevents flashing the wrong page while data loads
      if (urlState && urlState.page === 'edit') {
        document.getElementById('photos-page')?.classList.add('hidden');
        document.getElementById('edit-page')?.classList.remove('hidden');
        document.getElementById('export-page')?.classList.add('hidden');
      } else if (urlState && urlState.page === 'export') {
        document.getElementById('photos-page')?.classList.add('hidden');
        document.getElementById('edit-page')?.classList.add('hidden');
        document.getElementById('export-page')?.classList.remove('hidden');
      }
      // Photos page is visible by default in HTML, so no need to change anything for it
      
      // Initialize database
      await this.stateManager.initDB();
      console.log('DB initialized');
      
      // Load saved state
      await this.stateManager.loadState();
      console.log('State loaded');
      
      // Initialize export badges with current queue count
      const initialState = this.editorState.get();
      const initialQueueCount = (initialState.exportQueueItems || []).length;
      this.uiManager.updateExportBadges(initialQueueCount);
      
      // Register modals
      this.registerModals();
      
      // Setup event listeners
      this.setupEventListeners();
      console.log('Event listeners set up');
      
      // Get saved folder from localStorage to enable parallel loading
      const savedFolderId = localStorage.getItem('tinygrid_currentFolder');
      const savedFolderIdInt = savedFolderId ? parseInt(savedFolderId, 10) : null;
      
      // Get saved active photo for this folder
      let savedPhotoId = null;
      if (savedFolderIdInt && !isNaN(savedFolderIdInt)) {
        savedPhotoId = this.stateManager.loadActivePhotoFromLocalStorage(savedFolderIdInt);
      }
      
      // Start loading folders, photos metadata, AND full photo data in parallel
      const foldersPromise = this.folderManager.loadFolders();
      let photosPromise = null;
      let fullPhotoPromise = null;
      
      if (savedFolderIdInt && !isNaN(savedFolderIdInt)) {
        // Start loading photos metadata for the saved folder immediately (non-blocking)
        console.log(`[Performance] Pre-loading photos metadata for saved folder ${savedFolderIdInt} in parallel`);
        photosPromise = this.stateManager.photoService.getBasicInfo(savedFolderIdInt).catch(err => {
          console.warn('Failed to preload photos metadata:', err);
          return [];
        });
        
        // Also preload full data for the active photo
        if (savedPhotoId) {
          console.log(`[Performance] Pre-loading full data for saved photo ${savedPhotoId} in parallel`);
          fullPhotoPromise = this.stateManager.photoService.getFullInfo([savedPhotoId]).catch(err => {
            console.warn('Failed to preload full photo data:', err);
            return [];
          });
        }
      }
      
      // Wait for folders to load
      await foldersPromise;
      console.log('Folders loaded:', this.folderManager.getFolders());
      
      // Wait for photos to finish loading if started
      if (photosPromise) {
        await photosPromise;
        console.log('[Performance] Photos metadata preloaded');
      }
      
      // Wait for full photo data to finish loading if started
      if (fullPhotoPromise) {
        await fullPhotoPromise;
        console.log('[Performance] Full photo data preloaded for photo', savedPhotoId);
        console.log('[Performance] Cache check:', {
          hasPhoto: !!this.stateManager.photoService.getPhotoById(savedPhotoId),
          hasFullData: this.stateManager.photoService.hasFullData(savedPhotoId)
        });
      }
      
      // Update export options after folders are loaded
      this.updateSaveWithOriginalFilesOption();
      
      // Attach listeners to existing FolderStates
      const folderStateManager = this.stateManager.getFolderStateManager();
      folderStateManager.getAllFolderStates().forEach(folderState => {
        this.uiManager.attachFolderStateListeners(folderState);
      });
      
      // Render folder list
      this.uiManager.renderFolderList();
      
      // Parse URL and restore state
      await this.restoreFromUrl();
      
      // Setup browser history
      this.setupBrowserHistory();
      
      // Initialize dropdowns AFTER restoring state
      // This ensures PreviewBox mode is set and photos are loaded
      await this.initPreviewOptions();
      this.initUserMenu();
      
      console.log('EditorApp initialized successfully');
    } catch (error) {
      console.error('Error initializing EditorApp:', error);
    }
  }

  /**
   * Register all modals with the modal manager
   */
  registerModals() {
    const authModal = document.getElementById('auth-modal');
    const folderNameModal = document.getElementById('folder-name-modal');
    const contextMenu = document.getElementById('context-menu');
    
    console.log('Registering modals:', { authModal: !!authModal, folderNameModal: !!folderNameModal });
    
    if (authModal) this.modalManager.register('auth-modal', authModal);
    if (folderNameModal) this.modalManager.register('folder-name-modal', folderNameModal);
    if (contextMenu) this.modalManager.register('context-menu', contextMenu);
    
    console.log('Registered modals:', Array.from(this.modalManager.modals.keys()));
  }

  /**
   * Restore application state from URL
   */
  async restoreFromUrl() {
    const state = this.editorState.get();
    const urlState = this.stateManager.parseUrl();
    
    if (urlState) {
      if (urlState.photoId) {
        // If URL has a photo ID, check cache first (might be preloaded)
        console.log(`[Performance] restoreFromUrl: Checking cache for photo ${urlState.photoId}`);
        let photo = this.stateManager.photoService.getPhotoById(urlState.photoId);
        const hasFullData = this.stateManager.photoService.hasFullData(urlState.photoId);
        
        console.log(`[Performance] Cache status:`, { hasPhoto: !!photo, hasFullData });
        
        // Only fetch if not cached with full data
        if (!photo || !hasFullData) {
          console.log(`[Performance] Fetching photo ${urlState.photoId} from server`);
          try {
            const photos = await this.stateManager.photoService.getFullInfo([urlState.photoId]);
            if (photos.length > 0) {
              photo = photos[0];
            }
          } catch (error) {
            console.error('Error loading photo from URL:', error);
            this.uiManager.showPage(urlState.page);
            this.stateManager.updateUrl(urlState.page, null, false);
            return;
          }
        } else {
          console.log('[Performance] Photo already preloaded, using cache');
        }
        
        if (photo) {
          // Find and select the folder containing this photo
          const folder = this.folderManager.getFolders().find(f => f.id === photo.folder_id);
          
          if (folder) {
            await this.folderManager.selectFolder(folder.id);
            
            // Photo is already loaded, just get it from service
            const loadedPhoto = this.stateManager.photoService.getPhotoById(urlState.photoId);
            
            if (loadedPhoto) {
              // Force the page to show 'edit' first, then load the photo
              this.uiManager.showPage('edit');
              this.photoManager.editPhoto(loadedPhoto, false);
            }
          }
        } else {
          // Photo not found, just show the page
          this.uiManager.showPage(urlState.page);
          this.stateManager.updateUrl(urlState.page, null, false);
        }
      } else {
        // No photo ID, just show the page from URL
        this.uiManager.showPage(urlState.page);
        this.stateManager.updateUrl(urlState.page, null, false);
        
        // If we're on the edit page but no folder is selected, select the first folder
        if (urlState.page === 'edit' && !state.currentFolderId) {
          const folders = this.folderManager.getFolders();
          if (folders.length > 0) {
            console.log('[App] Edit page loaded with no folder, selecting first folder');
            await this.folderManager.selectFolder(folders[0].id);
          }
        }
      }
    } else {
      // No URL state - restore from saved state
      
      // Restore folder if we have one
      if (state.currentFolderId) {
        // Check if folder still exists before trying to select it
        const folderStateManager = this.stateManager.getFolderStateManager();
        const folderState = folderStateManager.getFolderState(state.currentFolderId);
        if (folderState) {
          await this.folderManager.selectFolder(state.currentFolderId);
        } else {
          // Folder no longer exists, clear it from state
          this.editorState.set({ currentFolderId: null });
        }
      }
      
      // Restore photo and page if on edit page
      if (state.activePhotoId && state.currentPage === 'edit') {
        const photo = this.photoManager.getPhotos().find(p => p.id === state.activePhotoId);
        if (photo) {
          // Explicitly show edit page and load photo
          this.uiManager.showPage('edit');
          this.photoManager.editPhoto(photo, false);
        } else {
          // Photo not found, just show photos page
          this.uiManager.showPage('photos');
        }
      } else {
        // Show photos page by default
        this.uiManager.showPage('photos');
      }
      
      this.stateManager.updateUrl(this.editorState.get().currentPage, null, false);
    }
    
    // Try to restore selected folder (if not already restored above)
    if (state.currentFolderId && urlState?.photoId) {
      // We already selected the folder when loading the photo from URL
      // No need to do it again
    } else if (state.currentFolderId && !urlState) {
      // Already handled in the else block above
    } else if (state.currentFolderId && urlState && !urlState.photoId) {
      // URL specified a page but no photo, restore the folder
      // Check if folder still exists before trying to select it
      const folderStateManager = this.stateManager.getFolderStateManager();
      const folderState = folderStateManager.getFolderState(state.currentFolderId);
      if (folderState) {
        await this.folderManager.selectFolder(state.currentFolderId);
      } else {
        // Folder no longer exists, clear it from state
        this.editorState.set({ currentFolderId: null });
      }
    }
  }

  /**
   * Setup browser history (back/forward buttons)
   */
  setupBrowserHistory() {
    window.addEventListener('popstate', (e) => {
      if (e.state) {
        if (e.state.photoId) {
          const photo = this.photoManager.getPhotos().find(p => p.id === e.state.photoId);
          if (photo) {
            this.photoManager.editPhoto(photo, false);
          }
        } else {
          this.uiManager.showPage(e.state.page);
          this.stateManager.updateUrl(e.state.page, null, false);
        }
      } else {
        const urlState = this.stateManager.parseUrl();
        if (urlState) {
          this.uiManager.showPage(urlState.page);
          this.stateManager.updateUrl(urlState.page, null, false);
        }
      }
    });
  }

  /**
   * Setup all event listeners
   */
  setupEventListeners() {
    // Keyboard shortcuts
    this.keyboardManager.setupKeyboardShortcuts();
    
    // View manager event listeners
    this.viewManager.setupEventListeners();
    
    // Window resize handler (debounced)
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const state = this.editorState.get();
        
        // Re-render photo selector to recalculate centering
        if (state.currentPage === 'edit' && state.activePhotoId) {
          const activePhoto = this.photoManager.getPhotoById(state.activePhotoId);
          if (activePhoto) {
            this.uiManager.renderPhotoSelector(
              this.photoManager.getPhotos(),
              activePhoto,
              state.selectedPhotoIds,
              activePhoto
            );
          }
        }
        
        // Photo grid auto-adjusts with CSS grid, but force a repaint if needed
        if (state.currentPage === 'photos' && this.photoManager.getPhotos().length > 0) {
          const photoGrid = document.getElementById('photo-grid');
          if (photoGrid) {
            void photoGrid.offsetHeight; // Trigger reflow
          }
        }
      }, 150);
    });
    
    // Tab navigation
    document.querySelectorAll('.tab-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const page = btn.dataset.page;
        this.uiManager.showPage(page);
        this.stateManager.updateUrl(page, this.editorState.get().currentPhoto);
        this.stateManager.saveState();
      });
    });
    
    // Mobile menu toggle
    document.getElementById('mobile-menu-btn')?.addEventListener('click', () => {
      document.getElementById('mobile-menu')?.classList.toggle('hidden');
    });
    
    document.getElementById('mobile-menu-close')?.addEventListener('click', () => {
      document.getElementById('mobile-menu')?.classList.add('hidden');
    });
    
    document.querySelectorAll('.mobile-nav-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const page = btn.dataset.page;
        this.uiManager.showPage(page);
        this.stateManager.updateUrl(page, this.editorState.get().currentPhoto);
        document.getElementById('mobile-menu')?.classList.add('hidden');
        this.stateManager.saveState();
      });
    });
    
    // Folder actions
    this.setupFolderEventListeners();
    
    // Photo grid interactions
    this.setupPhotoEventListeners();
    
    // Photo selector (filmstrip) interactions
    this.setupPhotoSelectorEventListeners();
    
    // Auth modal
    this.setupAuthEventListeners();
    
    // User menu
    this.setupUserMenuEventListeners();
    
    // Export actions
    this.setupExportEventListeners();
    
    // File drop zone
    this.setupDropZone();
  }

  /**
   * Setup folder event listeners
   */
  setupFolderEventListeners() {
    // Add folder button
    document.getElementById('add-folder-btn')?.addEventListener('click', () => {
      this.folderManager.addFolder();
    });
    
    // Folder creation modal
    document.getElementById('create-cloud-folder-btn')?.addEventListener('click', () => {
      const name = document.getElementById('folder-name-input')?.value.trim();
      if (name) {
        this.folderManager.createCloudFolder(name);
        this.modalManager.hide('folder-name-modal');
        document.getElementById('folder-name-input').value = '';
      }
    });
    
    document.getElementById('cancel-folder-btn')?.addEventListener('click', () => {
      this.modalManager.hide('folder-name-modal');
      document.getElementById('folder-name-input').value = '';
    });
    
    // Folder list interactions (using event delegation)
    document.getElementById('folder-list')?.addEventListener('click', (e) => {
      const folderItem = e.target.closest('.folder-item');
      if (folderItem) {
        const folderId = parseInt(folderItem.dataset.folderId);
        this.folderManager.selectFolder(folderId);
      }
    });
    
    // Folder context menu (right-click)
    document.getElementById('folder-list')?.addEventListener('contextmenu', (e) => {
      const folderItem = e.target.closest('.folder-item');
      if (folderItem) {
        e.preventDefault();
        const folderId = parseInt(folderItem.dataset.folderId);
        const folder = this.folderManager.getFolders().find(f => f.id === folderId);
        if (folder) {
          this.folderManager.showFolderContextMenu(folder, e.clientX, e.clientY);
        }
      }
    });
    
    // Folder long-press for mobile
    let pressTimer;
    document.getElementById('folder-list')?.addEventListener('touchstart', (e) => {
      const folderItem = e.target.closest('.folder-item');
      if (folderItem) {
        pressTimer = setTimeout(() => {
          e.preventDefault();
          const folderId = parseInt(folderItem.dataset.folderId);
          const folder = this.folderManager.getFolders().find(f => f.id === folderId);
          if (folder) {
            const touch = e.touches[0];
            this.folderManager.showFolderContextMenu(folder, touch.clientX, touch.clientY);
          }
        }, 500);
      }
    });
    
    document.getElementById('folder-list')?.addEventListener('touchend', () => {
      clearTimeout(pressTimer);
    });
    
    document.getElementById('folder-list')?.addEventListener('touchmove', () => {
      clearTimeout(pressTimer);
    });
  }

  /**
   * Setup click and double-click handlers with proper coordination
   * @param {HTMLElement} container - The container element to attach listeners to
   * @param {Function} onSingleClick - Handler for single clicks (photo, event) => void
   * @param {Function} onDoubleClick - Handler for double clicks (photo, event) => void
   */
  setupClickHandlers(container, onSingleClick, onDoubleClick) {
    let clickTimeout = null;
    let dblClickDetected = false;
    
    // Handle single clicks
    container.addEventListener('click', (e) => {
      const photoDiv = e.target.closest('[data-photo-id]');
      if (photoDiv) {
        const photoId = parseInt(photoDiv.dataset.photoId);
        // Try to get photo from cache first, then from metadata
        let photo = this.photoManager.getPhotoById(photoId);
        if (!photo) {
          // Get metadata if full data not available
          const state = this.editorState.get();
          const folderState = state.currentFolderId ? this.stateManager.getFolderStateManager().getFolderState(state.currentFolderId) : null;
          const photoMetadata = folderState?.getPhotoMetadata() || [];
          photo = photoMetadata.find(p => p.id === photoId);
        }
        
        if (photo && !dblClickDetected) {
          // Clear any pending timeout
          if (clickTimeout) {
            clearTimeout(clickTimeout);
            clickTimeout = null;
          }
          
          // Defer single click handler to allow double-click to be detected first
          clickTimeout = setTimeout(() => {
            if (!dblClickDetected) {
              onSingleClick(photo, e);
            }
            clickTimeout = null;
          }, 10); // Small delay to let dblclick event fire first
        }
      }
    });
    
    // Handle double-clicks
    if (onDoubleClick) {
      container.addEventListener('dblclick', (e) => {
        // Set flag immediately to prevent click handler from executing
        dblClickDetected = true;
        
        // Cancel any pending single click handlers
        if (clickTimeout) {
          clearTimeout(clickTimeout);
          clickTimeout = null;
        }
        
        const photoDiv = e.target.closest('[data-photo-id]');
        if (photoDiv) {
          const photoId = parseInt(photoDiv.dataset.photoId);
          // Try to get photo from cache first, then from metadata
          let photo = this.photoManager.getPhotoById(photoId);
          if (!photo) {
            // Get metadata if full data not available
            const state = this.editorState.get();
            const folderState = state.currentFolderId ? this.stateManager.getFolderStateManager().getFolderState(state.currentFolderId) : null;
            const photoMetadata = folderState?.getPhotoMetadata() || [];
            photo = photoMetadata.find(p => p.id === photoId);
          }
          
          if (photo) {
            onDoubleClick(photo, e);
          }
        }
        
        // Reset flag after a short delay
        setTimeout(() => {
          dblClickDetected = false;
        }, 300);
      });
    }
  }

  /**
   * Setup photo grid event listeners
   */
  setupPhotoEventListeners() {
    const photoGrid = document.getElementById('photo-grid');
    if (!photoGrid) return;
    
    this.setupClickHandlers(
      photoGrid,
      // Single click handler - update selection (state listeners will handle render)
      (photo, e) => {
        // Update selection - reactive state listeners will automatically update UI
        this.photoManager.handlePhotoClick(photo, e);
        
        // Schedule state save after state changes
        setTimeout(() => {
          this.stateManager.saveState();
        }, 100);
      },
      // Double click handler - navigate to Edit page
      (photo, e) => {
        this.photoManager.editPhoto(photo);
      }
    );
  }

  /**
   * Setup photo selector (filmstrip) event listeners
   */
  setupPhotoSelectorEventListeners() {
    const photoSelector = document.getElementById('photo-selector');
    if (!photoSelector) return;
    
    this.setupClickHandlers(
      photoSelector,
      // Single click handler - switch to photo or handle modifier keys
      async (photo, e) => {
        const state = this.editorState.get();
        
        // Handle selection with modifier keys (like Photos page)
        if (e.metaKey || e.ctrlKey) {
          // Cmd/Ctrl+Click: Toggle selection and switch to photo
          const previousPhotoId = state.activePhotoId;
          const wasOnlyOneSelected = state.selectedPhotoIds.size === 1 && 
                                      previousPhotoId && 
                                      state.selectedPhotoIds.has(previousPhotoId) && 
                                      previousPhotoId !== photo.id;
          
          // If starting multi-select, ensure both photos end up selected
          if (wasOnlyOneSelected) {
            // Add the clicked photo to selection first (before editPhoto overwrites it)
            this.editorState.addToPhotoSelection([photo.id]);
          }
          
          // editPhoto() will handle lazy loading
          // It won't modify selection since photo is already in it
          await this.photoManager.editPhoto(photo, false, false);
          
          // If not starting multi-select, toggle as normal
          if (!wasOnlyOneSelected) {
            this.editorState.togglePhotoSelection(photo.id);
          }
          
          this.stateManager.updateUrl('edit', photo);
        } else if (e.shiftKey && state.activePhotoId) {
          // Shift+Click: Select range and switch to photo
          const photos = this.photoManager.getPhotos();
          const activeIndex = photos.findIndex(p => p.id === state.activePhotoId);
          const clickedIndex = photos.findIndex(p => p.id === photo.id);
          
          if (activeIndex !== -1 && clickedIndex !== -1) {
            const start = Math.min(activeIndex, clickedIndex);
            const end = Math.max(activeIndex, clickedIndex);
            
            const rangePhotoIds = [];
            for (let i = start; i <= end; i++) {
              rangePhotoIds.push(photos[i].id);
            }
            
            // editPhoto() will handle lazy loading if needed
            await this.photoManager.editPhoto(photo, false, false);
            this.editorState.selectPhotoRange(rangePhotoIds);
            this.stateManager.updateUrl('edit', photo);
          }
        } else {
          // Plain click: Switch to this photo (make it active)
          // editPhoto() handles lazy loading and reactive UI updates
          await this.photoManager.editPhoto(photo, true, false);
        }
        
        // Save state after a delay (no need to manually re-render, state listeners handle it)
        setTimeout(() => {
          this.stateManager.saveState();
        }, 250);
      }
      // No double-click handler for photo selector
    );
  }

  /**
   * Setup authentication event listeners
   */
  setupAuthEventListeners() {
    // Desktop auth button
    document.getElementById('auth-btn')?.addEventListener('click', () => {
      this.modalManager.show('auth-modal');
    });
    
    // Mobile auth button
    document.getElementById('mobile-auth-btn')?.addEventListener('click', () => {
      this.modalManager.show('auth-modal');
      // Close mobile menu when opening auth modal
      document.getElementById('mobile-menu')?.classList.add('hidden');
    });
    
    // Fixed: correct ID for close button
    document.getElementById('auth-modal-close')?.addEventListener('click', () => {
      this.modalManager.hide('auth-modal');
    });
    
    // Sign Up Form
    const signUpForm = document.getElementById('signup-form');
    if (signUpForm) {
      signUpForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        await this.handleSignUp(new FormData(e.target));
      });
    }
    
    // Sign In Form  
    const signInForm = document.getElementById('signin-form');
    if (signInForm) {
      signInForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        await this.handleSignIn(new FormData(e.target));
      });
    }
    
    // Password Reset Form
    const resetForm = document.getElementById('reset-form');
    if (resetForm) {
      resetForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        await this.handlePasswordReset(new FormData(e.target));
      });
    }
    
    // Tab switching
    document.getElementById('signin-tab')?.addEventListener('click', () => {
      this.showAuthTab('signin');
    });
    
    document.getElementById('signup-tab')?.addEventListener('click', () => {
      this.showAuthTab('signup');
    });
    
    document.getElementById('forgot-password-link')?.addEventListener('click', () => {
      this.showAuthTab('reset');
    });
    
    document.getElementById('back-to-signin-link')?.addEventListener('click', () => {
      this.showAuthTab('signin');
    });
  }

  /**
   * Setup user menu event listeners
   */
  setupUserMenuEventListeners() {
    // User menu is now handled by DropdownMenu in initUserMenu()
    // Event listeners are automatically attached
  }

  /**
   * Setup export event listeners
   */
  setupExportEventListeners() {
    // Export button on Edit page
    document.getElementById('export-btn')?.addEventListener('click', async () => {
      const state = this.editorState.get();
      if (state.activePhotoId) {
        // Check if photo is already in queue
        if (this.uiManager.isPhotoInQueue(state.activePhotoId)) {
          this.showToast('Photo is already in export queue', 'info');
          return;
        }
        
        try {
          await this.exportQueueManager.addToQueue([state.activePhotoId]);
          this.showToast('Added to export queue');
        } catch (error) {
          console.error('[Export] Error adding photo to queue:', error);
          this.showToast('Error adding to export queue', 'error');
        }
      }
    });
    
    // Export Selected button on Photos page
    document.getElementById('export-selected-btn')?.addEventListener('click', async () => {
      const state = this.editorState.get();
      const selectedIds = Array.from(state.selectedPhotoIds);
      
      if (selectedIds.length > 0) {
        // Filter out photos already in queue
        const newIds = selectedIds.filter(id => !this.uiManager.isPhotoInQueue(id));
        
        if (newIds.length === 0) {
          this.showToast('All selected photos are already in export queue', 'info');
          return;
        }
        
        try {
          await this.exportQueueManager.addToQueue(newIds);
          const skipped = selectedIds.length - newIds.length;
          let message = `Added ${newIds.length} photo${newIds.length > 1 ? 's' : ''} to export queue`;
          if (skipped > 0) {
            message += ` (${skipped} already in queue)`;
          }
          this.showToast(message);
        } catch (error) {
          console.error('[Export] Error adding photos to queue:', error);
          this.showToast('Error adding to export queue', 'error');
        }
      }
    });
    
    // Enable/disable export buttons based on selection
    this.editorState.addEventListener('selectedPhotoIds', (e) => {
      const selectedIds = e.detail.value;
      const exportBtn = document.getElementById('export-selected-btn');
      const syncBtn = document.getElementById('sync-selected-btn');
      
      if (selectedIds && selectedIds.size > 0) {
        exportBtn?.removeAttribute('disabled');
        exportBtn?.setAttribute('title', `Export ${selectedIds.size} photo${selectedIds.size > 1 ? 's' : ''}`);
        syncBtn?.removeAttribute('disabled');
        syncBtn?.setAttribute('title', `Sync ${selectedIds.size} photo${selectedIds.size > 1 ? 's' : ''}`);
      } else {
        exportBtn?.setAttribute('disabled', '');
        exportBtn?.setAttribute('title', 'Select photos to export');
        syncBtn?.setAttribute('disabled', '');
        syncBtn?.setAttribute('title', 'Select photos to sync');
      }
    });
    
    // Save location radio buttons
    document.querySelectorAll('input[name="save-location"]').forEach(radio => {
      radio.addEventListener('change', (e) => {
        const location = e.target.value;
        this.editorState.set({ exportSaveLocation: location });
        
        // Update selected class on labels for visual feedback
        document.querySelectorAll('.save-location-option').forEach(label => {
          label.classList.remove('selected');
        });
        e.target.closest('.save-location-option')?.classList.add('selected');
        
        // Show/hide folder select button
        const selectFolderBtn = document.getElementById('select-export-folder-btn');
        if (location === 'folder') {
          selectFolderBtn?.classList.remove('hidden');
        } else {
          selectFolderBtn?.classList.add('hidden');
        }
        
        this.uiManager.updateFilenamePatternPreview();
      });
    });
    
    // Select export folder button
    document.getElementById('select-export-folder-btn')?.addEventListener('click', async () => {
      try {
        if (window.showDirectoryPicker) {
          const folderHandle = await window.showDirectoryPicker({ mode: 'readwrite' });
          this.editorState.set({ exportTargetFolderHandle: folderHandle });
          
          // Show selected folder name
          const selectedFolderName = document.getElementById('selected-folder-name');
          if (selectedFolderName) {
            selectedFolderName.textContent = `Selected: ${folderHandle.name}`;
            selectedFolderName.classList.remove('hidden');
          }
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('[Export] Error selecting folder:', error);
          this.showToast('Error selecting folder', 'error');
        }
      }
    });
    
    // Filename pattern input
    const filenamePatternInput = document.getElementById('filename-pattern-input');
    filenamePatternInput?.addEventListener('input', (e) => {
      this.editorState.set({ exportFilenamePattern: e.target.value });
      this.uiManager.updateFilenamePatternPreview();
    });
    
    // Start export button
    document.getElementById('start-export-btn')?.addEventListener('click', async () => {
      try {
        await this.exportRenderer.startExport();
        this.showToast('Export completed!', 'success');
      } catch (error) {
        console.error('[Export] Error starting export:', error);
        this.showToast(`Export failed: ${error.message}`, 'error');
      }
    });
    
    // Cancel export button
    document.getElementById('cancel-export-btn')?.addEventListener('click', () => {
      this.exportRenderer.cancelExport();
      this.showToast('Export cancelled');
    });
    
    // Clear completed button
    document.getElementById('clear-completed-btn')?.addEventListener('click', async () => {
      try {
        await this.exportQueueManager.clearCompleted();
        this.showToast('Cleared completed exports');
      } catch (error) {
        console.error('[Export] Error clearing completed:', error);
        this.showToast('Error clearing completed', 'error');
      }
    });
    
    // Remove queue item buttons (event delegation)
    document.getElementById('export-queue-list')?.addEventListener('click', async (e) => {
      const removeBtn = e.target.closest('.remove-queue-item-btn');
      if (removeBtn) {
        const itemId = parseInt(removeBtn.dataset.itemId);
        try {
          await this.exportQueueManager.removeFromQueue(itemId);
        } catch (error) {
          console.error('[Export] Error removing from queue:', error);
          this.showToast('Error removing from queue', 'error');
        }
      }
    });
    
    // Listen for state changes to update export page
    this.editorState.addEventListener('exportQueueItems', () => {
      const state = this.editorState.get();
      
      // Always update the badges regardless of current page
      const queueItems = state.exportQueueItems || [];
      this.uiManager.updateExportBadges(queueItems.length);
      
      // Update export button states
      this.uiManager.updateExportButtonState();
      
      // Re-render export page if currently viewing it
      if (state.currentPage === 'export') {
        this.uiManager.renderExportPage();
      }
    });
    
    // Listen for active photo changes to update export button state
    this.editorState.addEventListener('activePhotoId', () => {
      this.uiManager.updateExportButtonState();
    });
    
    this.editorState.addEventListener('activeExports', () => {
      const state = this.editorState.get();
      if (state.currentPage === 'export' && state.isExporting) {
        this.uiManager.updateExportProgress();
      }
    });
    
    this.editorState.addEventListener('isExporting', () => {
      const state = this.editorState.get();
      if (state.currentPage === 'export') {
        this.uiManager.renderExportPage();
      }
    });
    
    // Fetch export queue on init
    this.exportQueueManager.fetchQueue().catch(error => {
      console.error('[Export] Error fetching queue:', error);
    });
    
    // Mobile detection for export options
    this.setupExportMobileDetection();
  }

  /**
   * Setup mobile detection for export options
   */
  setupExportMobileDetection() {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const hasFileSystemAPI = !!window.showDirectoryPicker;
    
    // Check if we have any granted folders
    this.updateSaveWithOriginalFilesOption();
    
    // Listen for folder changes to update the option
    // Note: Folders are managed by folderStateManager, not in main state
    // We'll check on page navigation to export page instead
    
    // Hide "Download to Folder" on mobile or if API not available
    const folderContainer = document.getElementById('save-location-folder-container');
    const folderBadge = document.getElementById('folder-availability-badge');
    
    if (isMobile || !hasFileSystemAPI) {
      if (folderContainer) {
        const label = folderContainer.querySelector('label');
        if (label) {
          label.classList.add('opacity-50', 'cursor-not-allowed');
          label.classList.remove('cursor-pointer', 'hover:border-gray-600');
        }
        const radio = folderContainer.querySelector('input[type="radio"]');
        if (radio) {
          radio.disabled = true;
        }
      }
      
      if (folderBadge) {
        folderBadge.textContent = isMobile ? '⚠️ Not available on mobile' : '⚠️ Browser not supported';
        folderBadge.classList.remove('text-green-400');
        folderBadge.classList.add('text-yellow-400');
      }
    }
  }

  /**
   * Update "Save with Original Files" option visibility based on granted folders
   */
  updateSaveWithOriginalFilesOption() {
    const hasFileSystemAPI = !!window.showDirectoryPicker;
    const originalContainer = document.getElementById('save-location-original-container');
    
    // Get folders from folder manager
    const folders = this.folderManager?.getFolders() || [];
    
    console.log('[Export] Checking Save with Original Files availability:', {
      hasFileSystemAPI,
      hasFolders: folders.length > 0,
      folderCount: folders.length,
      folders: folders.map(f => ({ id: f.id, name: f.name, type: f.folder_type }))
    });
    
    if (!originalContainer) {
      console.log('[Export] Original container element not found');
      return;
    }
    
    // Check if we have any granted folders (folder_type === 0 OR === "granted")
    const grantedFolders = folders.filter(f => f.folder_type === 0 || f.folder_type === 'granted');
    const hasGrantedFolders = grantedFolders.length > 0;
    
    console.log('[Export] Granted folders:', {
      count: grantedFolders.length,
      folders: grantedFolders.map(f => ({ id: f.id, name: f.name }))
    });
    
    if (hasGrantedFolders && hasFileSystemAPI) {
      console.log('[Export] ✓ Showing "Save with Original Files" option');
      originalContainer.classList.remove('hidden');
    } else {
      console.log('[Export] ✗ Hiding "Save with Original Files" option', {
        reason: !hasGrantedFolders ? 'No granted folders' : 'File System API not available'
      });
      originalContainer.classList.add('hidden');
      // If this option was selected, switch to zip
      const originalRadio = originalContainer.querySelector('input[type="radio"]');
      if (originalRadio?.checked) {
        document.querySelector('input[value="zip"]')?.click();
      }
    }
  }

  /**
   * Show toast notification
   */
  showToast(message, type = 'info') {
    // Create toast element
    const toast = document.createElement('div');
    toast.className = `fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg text-white z-50 transition-opacity duration-300`;
    
    if (type === 'error') {
      toast.className += ' bg-red-600';
    } else if (type === 'success') {
      toast.className += ' bg-green-600';
    } else {
      toast.className += ' bg-blue-600';
    }
    
    toast.textContent = message;
    document.body.appendChild(toast);
    
    // Remove after 3 seconds
    setTimeout(() => {
      toast.style.opacity = '0';
      setTimeout(() => {
        document.body.removeChild(toast);
      }, 300);
    }, 3000);
  }

  /**
   * Setup drop zone for file uploads
   */
  setupDropZone() {
    const dropZone = document.getElementById('photo-grid');
    if (!dropZone) return;
    
    dropZone.addEventListener('dragover', (e) => {
      e.preventDefault();
      dropZone.classList.add('bg-gray-700');
    });
    
    dropZone.addEventListener('dragleave', () => {
      dropZone.classList.remove('bg-gray-700');
    });
    
    dropZone.addEventListener('drop', async (e) => {
      e.preventDefault();
      dropZone.classList.remove('bg-gray-700');
      
      const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
      if (files.length > 0) {
        await this.folderManager.handleDroppedFiles(files);
      }
    });
  }

  /**
   * Show auth tab (signin, signup, or reset)
   */
  showAuthTab(tab) {
    const signinForm = document.getElementById('signin-form');
    const signupForm = document.getElementById('signup-form');
    const resetForm = document.getElementById('reset-form');
    const signinTab = document.getElementById('signin-tab');
    const signupTab = document.getElementById('signup-tab');
    
    // Clear errors when switching tabs
    this.clearAuthError('signin-form');
    this.clearAuthError('signup-form');
    
    // Hide all forms
    signinForm?.classList.add('hidden');
    signupForm?.classList.add('hidden');
    resetForm?.classList.add('hidden');
    
    // Reset tab styles
    signinTab?.classList.remove('border-blue-500', 'text-blue-600', 'font-semibold');
    signinTab?.classList.add('border-transparent', 'text-gray-500');
    signupTab?.classList.remove('border-blue-500', 'text-blue-600', 'font-semibold');
    signupTab?.classList.add('border-transparent', 'text-gray-500');
    
    // Show selected form and update tab
    if (tab === 'signin') {
      signinForm?.classList.remove('hidden');
      signinTab?.classList.remove('border-transparent', 'text-gray-500');
      signinTab?.classList.add('border-blue-500', 'text-blue-600', 'font-semibold');
    } else if (tab === 'signup') {
      signupForm?.classList.remove('hidden');
      signupTab?.classList.remove('border-transparent', 'text-gray-500');
      signupTab?.classList.add('border-blue-500', 'text-blue-600', 'font-semibold');
    } else if (tab === 'reset') {
      resetForm?.classList.remove('hidden');
      // No tab highlight for reset form
    }
  }

  /**
   * Show error message in a specific form
   * @param {string} formId - The form ID ('signin-form', 'signup-form')
   * @param {string} message - The error message to display
   */
  showAuthError(formId, message) {
    const errorId = formId === 'signin-form' ? 'signin-error' : 'signup-error';
    const errorDiv = document.getElementById(errorId);
    if (errorDiv) {
      errorDiv.textContent = message;
      errorDiv.classList.remove('hidden');
    }
  }

  /**
   * Clear error message in a specific form
   * @param {string} formId - The form ID ('signin-form', 'signup-form')
   */
  clearAuthError(formId) {
    const errorId = formId === 'signin-form' ? 'signin-error' : 'signup-error';
    const errorDiv = document.getElementById(errorId);
    if (errorDiv) {
      errorDiv.classList.add('hidden');
      errorDiv.textContent = '';
    }
  }

  /**
   * Handle password reset
   */
  async handlePasswordReset(formData) {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      
      const response = await fetch('/passwords', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          email_address: formData.get('email')
        })
      });

      const messageDiv = document.getElementById('reset-message');
      if (response.ok) {
        messageDiv.textContent = 'Password reset instructions sent to your email';
        messageDiv.classList.remove('hidden', 'text-red-600');
        messageDiv.classList.add('text-green-600');
      } else {
        const data = await response.json();
        messageDiv.textContent = data.error || 'Password reset failed';
        messageDiv.classList.remove('hidden', 'text-green-600');
        messageDiv.classList.add('text-red-600');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      const messageDiv = document.getElementById('reset-message');
      messageDiv.textContent = 'Password reset failed';
      messageDiv.classList.remove('hidden', 'text-green-600');
      messageDiv.classList.add('text-red-600');
    }
  }

  /**
   * Handle sign up
   */
  async handleSignUp(formData) {
    try {
      // Clear any previous errors
      this.clearAuthError('signup-form');
      
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      
      const response = await fetch('/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          user: {
            email_address: formData.get('email'),
            password: formData.get('password'),
            password_confirmation: formData.get('password_confirmation')
          }
        })
      });

      if (response.ok) {
        window.location.reload();
      } else {
        const data = await response.json();
        this.showAuthError('signup-form', data.error || 'Sign up failed');
      }
    } catch (error) {
      console.error('Sign up error:', error);
      this.showAuthError('signup-form', 'Sign up failed. Please try again.');
    }
  }

  /**
   * Handle sign in
   */
  async handleSignIn(formData) {
    try {
      // Clear any previous errors
      this.clearAuthError('signin-form');
      
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      
      const response = await fetch('/session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          email_address: formData.get('email'),
          password: formData.get('password')
        })
      });

      if (response.ok) {
        window.location.reload();
      } else {
        const data = await response.json();
        this.showAuthError('signin-form', data.error || 'Invalid email or password');
      }
    } catch (error) {
      console.error('Sign in error:', error);
      this.showAuthError('signin-form', 'Sign in failed. Please try again.');
    }
  }

  /**
   * Initialize user menu dropdown
   */
  initUserMenu() {
    const userMenuBtn = document.getElementById('user-menu-btn');
    if (!userMenuBtn) return; // User not logged in
    
    const userEmail = userMenuBtn.getAttribute('title') || '';
    
    // Get current values from editorState
    const currentInferenceSteps = this.editorState.get('inferenceSteps');
    const currentFullResInferenceSteps = this.editorState.get('fullResInferenceSteps');
    const currentFullResolutionSize = this.editorState.get('fullResolutionSize');
    const currentSeed = this.editorState.get('seed');
    const currentBaseShift = this.editorState.get('baseShift');
    const currentMaxShift = this.editorState.get('maxShift');
    const currentSampleEvery = this.editorState.get('sampleEvery');
    
    const items = [
      {
        label: userEmail,
        disabled: true
      },
      { separator: true },
      {
        customHTML: `
          <div class="space-y-2">
            <label class="block text-xs font-medium text-gray-400">Inference Steps (Preview)</label>
            <div class="flex items-center space-x-2">
              <input type="range" 
                     id="inference-steps-slider" 
                     min="2" 
                     max="30" 
                     value="${currentInferenceSteps}" 
                     class="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider-thumb"
                     style="background: linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((currentInferenceSteps - 2) / 28) * 100}%, #374151 ${((currentInferenceSteps - 2) / 28) * 100}%, #374151 100%);">
              <span id="inference-steps-value" class="text-xs text-gray-400 w-8 text-right">${currentInferenceSteps}</span>
            </div>
          </div>
        `,
        onMount: (container) => {
          const slider = container.querySelector('#inference-steps-slider');
          const valueDisplay = container.querySelector('#inference-steps-value');
          const state = this.editorState.get();
          const photo = this.photoManager.getPhotoById(state.activePhotoId);
          
          // Track drag state
          slider.addEventListener('mousedown', () => {
            this.editorState.setSliderDragging(true);
            
            // Add document-level mouseup handler (remove previous if exists)
            const mouseUpHandler = () => {
              this.editorState.setSliderDragging(false);
              
              // Dispatch event to trigger render on release
              this.editorState.dispatchEvent(new CustomEvent('sliderReleased', {
                detail: { sliderName: 'inferenceSteps' }
              }));
              
              // Remove handler after firing
              document.removeEventListener('mouseup', mouseUpHandler);
            };
            
            document.addEventListener('mouseup', mouseUpHandler);
          });
          
          slider.addEventListener('input', (e) => {
            const value = parseInt(e.target.value);
            valueDisplay.textContent = value;
            
            // Update gradient
            const percent = ((value - 2) / 28) * 100;
            e.target.style.background = `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${percent}%, #374151 ${percent}%, #374151 100%)`;
            
            // Update state value (but don't trigger render while dragging)
            if (photo) {
              this.sliderManager.updateInferenceSteps(photo, value);
            } else {
              this.editorState.set({ inferenceSteps: value });
            }
          });
        }
      },
      {
        customHTML: `
          <div class="space-y-2">
            <label class="block text-xs font-medium text-gray-400">Full Resolution Inference Steps</label>
            <div class="flex items-center space-x-2">
              <input type="range" 
                     id="fullres-inference-steps-slider" 
                     min="2" 
                     max="30" 
                     value="${currentFullResInferenceSteps}" 
                     class="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider-thumb"
                     style="background: linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((currentFullResInferenceSteps - 2) / 28) * 100}%, #374151 ${((currentFullResInferenceSteps - 2) / 28) * 100}%, #374151 100%);">
              <span id="fullres-inference-steps-value" class="text-xs text-gray-400 w-8 text-right">${currentFullResInferenceSteps}</span>
            </div>
          </div>
        `,
        onMount: (container) => {
          const slider = container.querySelector('#fullres-inference-steps-slider');
          const valueDisplay = container.querySelector('#fullres-inference-steps-value');
          const state = this.editorState.get();
          const photo = this.photoManager.getPhotoById(state.activePhotoId);
          
          // Track drag state
          slider.addEventListener('mousedown', () => {
            this.editorState.setSliderDragging(true);
            
            // Add document-level mouseup handler (remove previous if exists)
            const mouseUpHandler = () => {
              this.editorState.setSliderDragging(false);
              
              // Dispatch event to trigger render on release
              this.editorState.dispatchEvent(new CustomEvent('sliderReleased', {
                detail: { sliderName: 'fullResInferenceSteps' }
              }));
              
              // Remove handler after firing
              document.removeEventListener('mouseup', mouseUpHandler);
            };
            
            document.addEventListener('mouseup', mouseUpHandler);
          });
          
          slider.addEventListener('input', (e) => {
            const value = parseInt(e.target.value);
            valueDisplay.textContent = value;
            
            // Update gradient
            const percent = ((value - 2) / 28) * 100;
            e.target.style.background = `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${percent}%, #374151 ${percent}%, #374151 100%)`;
            
            // Update state value
            this.editorState.set({ fullResInferenceSteps: value });
          });
        }
      },
      {
        customHTML: `
          <div class="space-y-2">
            <label for="full-resolution-size" class="block text-xs font-medium text-gray-400">Full Resolution Size</label>
            <select id="full-resolution-size" 
                    class="w-full px-3 py-1 bg-gray-700 border border-gray-600 rounded text-sm text-white focus:outline-none focus:border-blue-500">
              <option value="512" ${currentFullResolutionSize === 512 ? 'selected' : ''}>512</option>
              <option value="768" ${currentFullResolutionSize === 768 ? 'selected' : ''}>768</option>
              <option value="1024" ${currentFullResolutionSize === 1024 ? 'selected' : ''}>1024</option>
            </select>
          </div>
        `,
        onMount: (container) => {
          const select = container.querySelector('#full-resolution-size');
          
          select.addEventListener('change', (e) => {
            const value = parseInt(e.target.value);
            // Update state - this will trigger re-render through event listener
            this.editorState.set({ fullResolutionSize: value });
          });
        }
      },
      {
        customHTML: `
          <div class="space-y-2 mt-3">
            <label for="seed-input" class="block text-xs font-medium text-gray-400">Seed</label>
            <input type="number" 
                   id="seed-input" 
                   value="${currentSeed}" 
                   class="w-full px-3 py-1 bg-gray-700 border border-gray-600 rounded text-sm text-white focus:outline-none focus:border-blue-500">
          </div>
        `,
        onMount: (container) => {
          const input = container.querySelector('#seed-input');
          const state = this.editorState.get();
          const photo = this.photoManager.getPhotoById(state.activePhotoId);
          
          input.addEventListener('change', (e) => {
            const value = parseInt(e.target.value);
            if (!isNaN(value)) {
              // Update with debouncing and save
              if (photo) {
                this.sliderManager.updateSeed(photo, value);
              } else {
                this.editorState.set({ seed: value });
              }
            }
          });
        }
      },
      {
        customHTML: `
          <div class="space-y-2">
            <label class="block text-xs font-medium text-gray-400">Base Shift</label>
            <div class="flex items-center space-x-2">
              <input type="range" 
                     id="base-shift-slider" 
                     min="0" 
                     max="2" 
                     step="0.01"
                     value="${currentBaseShift}" 
                     class="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider-thumb"
                     style="background: linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(currentBaseShift / 2) * 100}%, #374151 ${(currentBaseShift / 2) * 100}%, #374151 100%);">
              <span id="base-shift-value" class="text-xs text-gray-400 w-12 text-right">${currentBaseShift.toFixed(2)}</span>
            </div>
          </div>
        `,
        onMount: (container) => {
          const slider = container.querySelector('#base-shift-slider');
          const valueDisplay = container.querySelector('#base-shift-value');
          
          // Track drag state
          slider.addEventListener('mousedown', () => {
            this.editorState.setSliderDragging(true);
            
            // Add document-level mouseup handler
            const mouseUpHandler = () => {
              this.editorState.setSliderDragging(false);
              
              // Dispatch event to trigger render on release
              this.editorState.dispatchEvent(new CustomEvent('sliderReleased', {
                detail: { sliderName: 'baseShift' }
              }));
              
              // Remove handler after firing
              document.removeEventListener('mouseup', mouseUpHandler);
            };
            
            document.addEventListener('mouseup', mouseUpHandler);
          });
          
          slider.addEventListener('input', (e) => {
            const value = parseFloat(e.target.value);
            valueDisplay.textContent = value.toFixed(2);
            
            // Update gradient
            const percent = (value / 2) * 100;
            e.target.style.background = `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${percent}%, #374151 ${percent}%, #374151 100%)`;
            
            // Update state value
            this.editorState.set({ baseShift: value });
          });
        }
      },
      {
        customHTML: `
          <div class="space-y-2">
            <label class="block text-xs font-medium text-gray-400">Max Shift</label>
            <div class="flex items-center space-x-2">
              <input type="range" 
                     id="max-shift-slider" 
                     min="0" 
                     max="2" 
                     step="0.01"
                     value="${currentMaxShift}" 
                     class="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider-thumb"
                     style="background: linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(currentMaxShift / 2) * 100}%, #374151 ${(currentMaxShift / 2) * 100}%, #374151 100%);">
              <span id="max-shift-value" class="text-xs text-gray-400 w-12 text-right">${currentMaxShift.toFixed(2)}</span>
            </div>
          </div>
        `,
        onMount: (container) => {
          const slider = container.querySelector('#max-shift-slider');
          const valueDisplay = container.querySelector('#max-shift-value');
          
          // Track drag state
          slider.addEventListener('mousedown', () => {
            this.editorState.setSliderDragging(true);
            
            // Add document-level mouseup handler
            const mouseUpHandler = () => {
              this.editorState.setSliderDragging(false);
              
              // Dispatch event to trigger render on release
              this.editorState.dispatchEvent(new CustomEvent('sliderReleased', {
                detail: { sliderName: 'maxShift' }
              }));
              
              // Remove handler after firing
              document.removeEventListener('mouseup', mouseUpHandler);
            };
            
            document.addEventListener('mouseup', mouseUpHandler);
          });
          
          slider.addEventListener('input', (e) => {
            const value = parseFloat(e.target.value);
            valueDisplay.textContent = value.toFixed(2);
            
            // Update gradient
            const percent = (value / 2) * 100;
            e.target.style.background = `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${percent}%, #374151 ${percent}%, #374151 100%)`;
            
            // Update state value
            this.editorState.set({ maxShift: value });
          });
        }
      },
      {
        customHTML: `
          <div class="space-y-2">
            <label class="block text-xs font-medium text-gray-400">Sample Every</label>
            <div class="flex items-center space-x-2">
              <input type="range" 
                     id="sample-every-slider" 
                     min="0" 
                     max="10" 
                     step="1"
                     value="${currentSampleEvery}" 
                     class="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider-thumb"
                     style="background: linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(currentSampleEvery / 10) * 100}%, #374151 ${(currentSampleEvery / 10) * 100}%, #374151 100%);">
              <span id="sample-every-value" class="text-xs text-gray-400 w-12 text-right">${currentSampleEvery}</span>
            </div>
          </div>
        `,
        onMount: (container) => {
          const slider = container.querySelector('#sample-every-slider');
          const valueDisplay = container.querySelector('#sample-every-value');
          
          // Track drag state
          slider.addEventListener('mousedown', () => {
            this.editorState.setSliderDragging(true);
            
            // Add document-level mouseup handler
            const mouseUpHandler = () => {
              this.editorState.setSliderDragging(false);
              
              // Dispatch event to trigger render on release
              this.editorState.dispatchEvent(new CustomEvent('sliderReleased', {
                detail: { sliderName: 'sampleEvery' }
              }));
              
              // Remove handler after firing
              document.removeEventListener('mouseup', mouseUpHandler);
            };
            
            document.addEventListener('mouseup', mouseUpHandler);
          });
          
          slider.addEventListener('input', (e) => {
            const value = parseInt(e.target.value);
            valueDisplay.textContent = value;
            
            // Update gradient
            const percent = (value / 10) * 100;
            e.target.style.background = `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${percent}%, #374151 ${percent}%, #374151 100%)`;
            
            // Update state value
            this.editorState.set({ sampleEvery: value });
          });
        }
      },
      { separator: true },
      {
        label: 'Logout',
        danger: true,
        icon: '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path></svg>',
        action: () => this.handleLogout()
      }
    ];

    // Initialize dropdown
    this.userMenuDropdown = new DropdownMenu({
      triggerId: 'user-menu-btn',
      menuId: 'user-menu',
      items: items,
      position: 'bottom-right'
    });
  }

  /**
   * Handle logout
   */
  async handleLogout() {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      
      await fetch('/session', {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': csrfToken
        }
      });
      
      window.location.reload();
    } catch (error) {
      console.error('Logout error:', error);
      window.location.reload();
    }
  }

  /**
   * Initialize preview options dropdown
   */
  async initPreviewOptions() {
    // Load current preview mode from backend
    let currentMode = 'tooltip';
    try {
      const response = await fetch('/api/v1/user/preferences');
      if (response.ok) {
        const data = await response.json();
        currentMode = data.preview_mode || 'tooltip';
      }
    } catch (error) {
      console.error('Error loading preview preferences:', error);
    }

    // Set initial mode on PreviewBox
    this.sliderManager.previewBox.setMode(currentMode);

    // Create dropdown items
    const items = [
      { label: 'Tool-tip', value: 'tooltip', selected: currentMode === 'tooltip' },
      { label: 'Drawer When Dragging', value: 'drawer-dragging', selected: currentMode === 'drawer-dragging' },
      { label: 'Drawer Always Active', value: 'drawer-always', selected: currentMode === 'drawer-always' }
    ];

    // Initialize dropdown
    this.previewOptionsDropdown = new DropdownMenu({
      triggerId: 'preview-options-btn',
      menuId: 'preview-options-menu',
      items: items,
      position: 'bottom-right',
      onSelect: (item, index) => {
        this.handlePreviewModeChange(item.value);
      }
    });

    // Show drawer if mode is drawer-always
    if (currentMode === 'drawer-always') {
      const state = this.editorState.get();
      if (state.currentPage === 'edit' && state.activePhotoId) {
        // If we have a photo, show it in the drawer with the Strength slider
        const activePhoto = this.photoManager.getPhotoById(state.activePhotoId);
        const strengthSlider = this.sliderManager.sliders.find(s => s.name === 'style');
        if (activePhoto && strengthSlider) {
          this.sliderManager.previewBox.showDrawer(activePhoto, strengthSlider, null);
        }
      } else {
        // No photo selected, show empty drawer
        this.sliderManager.previewBox.showEmptyDrawer();
      }
    }
  }

  /**
   * Handle preview mode change
   */
  async handlePreviewModeChange(mode) {
    // Update PreviewBox mode
    this.sliderManager.previewBox.setMode(mode);

    // Update dropdown items
    const items = [
      { label: 'Tool-tip', value: 'tooltip', selected: mode === 'tooltip' },
      { label: 'Drawer When Dragging', value: 'drawer-dragging', selected: mode === 'drawer-dragging' },
      { label: 'Drawer Always Active', value: 'drawer-always', selected: mode === 'drawer-always' }
    ];
    this.previewOptionsDropdown.updateItems(items);

    // Save to backend
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      await fetch('/api/v1/user/preferences', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          preferences: {
            preview_mode: mode
          }
        })
      });
    } catch (error) {
      console.error('Error saving preview mode:', error);
    }

    // Handle drawer-always mode
    if (mode === 'drawer-always') {
      const state = this.editorState.get();
      if (state.currentPage === 'edit' && state.activePhotoId) {
        // Show drawer for active photo with the Strength slider
        const activePhoto = this.photoManager.getPhotoById(state.activePhotoId);
        const strengthSlider = this.sliderManager.sliders.find(s => s.name === 'style');
        if (activePhoto && strengthSlider) {
          this.sliderManager.previewBox.showDrawer(activePhoto, strengthSlider, null);
        }
      } else {
        // No photo selected, show empty drawer
        this.sliderManager.previewBox.showEmptyDrawer();
      }
    }
  }
}

